import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { fetchFeatureCatalog } from '../../services/api/adminFeatureCatalog';
import { fetchOverrides, saveOverrides } from '../../services/api/adminFeatureOverrides';
import FeatureOverrideRow from './FeatureOverrideRow';

const WorkspaceOverrideDrawer = ({ open, workspace, onClose, onSaved }) => {
  const [catalog, setCatalog] = useState({ features: [] });
  const [overrides, setOverrides] = useState({});
  // "feature.sub_feature" codes the workspace gets with no override (plan + default-on).
  const [inheritedCodes, setInheritedCodes] = useState(new Set());
  const [pending, setPending] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!open || !workspace) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setPending({});
        const [catRes, ovRes] = await Promise.all([
          fetchFeatureCatalog(),
          fetchOverrides(workspace.id),
        ]);
        if (cancelled) return;
        setCatalog(catRes.data);
        const ovMap = {};
        (ovRes.data?.overrides || []).forEach((o) => { ovMap[o.sub_feature_id] = o; });
        setOverrides(ovMap);
        setInheritedCodes(new Set(ovRes.data?.inherited_codes || []));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, workspace?.id]);

  // Without an override the workspace inherits from its plan, so the catalog
  // defaults alone would show every plan-gated feature as off.
  const inheritedState = (feature, sf) => inheritedCodes.has(`${feature.code}.${sf.code}`);

  // Compute effective state for a sub_feature given current overrides + pending edits.
  const effectiveState = (feature, sf) => {
    if (pending[sf.id]) {
      const ps = pending[sf.id].state;
      if (ps === 'enabled') return true;
      if (ps === 'disabled') return false;
      if (ps === 'revert') return inheritedState(feature, sf);
    }
    if (overrides[sf.id]) {
      return overrides[sf.id].state === 'enabled';
    }
    return inheritedState(feature, sf);
  };

  const handleToggle = (feature, sf) => {
    const isOn = effectiveState(feature, sf);
    setPending((p) => ({
      ...p,
      [sf.id]: { state: isOn ? 'disabled' : 'enabled', reason: '' },
    }));
  };

  const handleRevert = (sf) => {
    setPending((p) => ({ ...p, [sf.id]: { state: 'revert' } }));
  };

  const handleChangeReason = (sf, reason) => {
    setPending((p) => ({ ...p, [sf.id]: { ...p[sf.id], reason } }));
  };

  const isDirty = Object.keys(pending).length > 0;

  const handleSave = async () => {
    if (!workspace) return;
    try {
      setSaving(true);
      setError(null);
      const changes = Object.entries(pending).map(([sfId, change]) => ({
        sub_feature_id: parseInt(sfId, 10),
        state: change.state,
        reason: change.reason || null,
      }));
      await saveOverrides(workspace.id, changes);
      setPending({});
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full max-w-xl bg-card shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{workspace?.name}</h3>
            <p className="text-xs text-muted-foreground">Per-workspace feature overrides</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-border flex items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={!showAll}
              onChange={() => setShowAll(false)}
              className="h-4 w-4"
            />
            Overrides only
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={showAll}
              onChange={() => setShowAll(true)}
              className="h-4 w-4"
            />
            All features (incl. inherited)
          </label>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && <div className="text-sm text-muted-foreground">Loading...</div>}
          {error && <div className="mb-3 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          {!loading && (catalog.features || []).map((f) => {
            const subs = (f.sub_features || []).filter((sf) => showAll || overrides[sf.id] || pending[sf.id]);
            if (subs.length === 0) return null;
            return (
              <div key={f.id} className="mb-4">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{f.label}</h4>
                {subs.map((sf) => (
                  <FeatureOverrideRow
                    key={sf.id}
                    subFeature={sf}
                    effectiveOn={effectiveState(f, sf)}
                    override={overrides[sf.id] || null}
                    pendingState={pending[sf.id]?.state || null}
                    pendingReason={pending[sf.id]?.reason}
                    onToggle={() => handleToggle(f, sf)}
                    onChangeReason={(v) => handleChangeReason(sf, v)}
                    onRevert={() => handleRevert(sf)}
                  />
                ))}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-foreground border border-input rounded-lg hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceOverrideDrawer;
